/*
===============================================================================================================
                                 Copyright 2025-2025, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

import { throttle } from 'lodash';
import { useEffect, useState } from 'react';

const defaultEvents = ['mousemove', 'mousedown', 'resize', 'keydown', 'touchstart', 'wheel'];
// token expires in 10 minutes, we set idle time to 9 minutes for banner to appear before token expires
const nineMinutes = 9 * 60 * 1000;

/**
 * Custom hook to detect user idle state.
 * @param {number} ms - The duration in milliseconds after which the user is considered idle
 * @param {boolean} initialState - Initial idle state
 * @param {Array} events - Array of events to listen for user activity
 * @returns {boolean} - Returns true if the user is idle, false otherwise
 */

const useIdle = (
  ms = nineMinutes,
  initialState = false,
  events = defaultEvents,
) => {
  const [isIdle, setIsIdle] = useState(initialState);

  useEffect(() => {
    let mounted = true;
    let idleTimeout;

    const setIdleState = (isIdleState) => {
      if (mounted) {
        setIsIdle(isIdleState);
      }
    };

    const handleEvent = throttle(() => {
      setIdleState(false);

      clearTimeout(idleTimeout);

      idleTimeout = setTimeout(() => setIdleState(true), ms);
    }, 500, { leading: true, trailing: false });

    for (let i = 0; i < events.length; i += 1) {
      window.addEventListener(events[i], handleEvent);
    }

    idleTimeout = setTimeout(() => setIdleState(true), ms);

    return () => {
      mounted = false;
      clearTimeout(idleTimeout);
      for (let i = 0; i < events.length; i += 1) {
        window.removeEventListener(events[i], handleEvent);
      }
    };
  }, [ms, events]);

  return { isIdle };
};

export default useIdle;
